import { put, all, takeEvery, fork, select, call } from "redux-saga/effects";
import { actions, getters } from "./model";
import {
  getShowMaxLength,
  getMaxLengthFormatted,
  getCurrentForm,
  hasFormErrors
} from "./selectors";
import { eventId as getEventId } from "redux/modules/event/selectors";
import { getCredentials } from "redux/modules/user/selectors";
import { orgId as getOrgId } from "redux/modules/organization/selectors";
import { registerError } from "redux/modules/errors/actions";
import { hideModal } from "redux/modules/modal/actions";
import { fieldGroups as getFieldGroups } from "redux/modules/modules/module/selectors";
import { apiCall } from "App/Data/sagas";
import Api from "./api";

import * as R from "ramda";

const getParams = function*() {
  const credentials = yield select(getCredentials);
  const orgId = yield select(getOrgId);
  const eventId = yield select(getEventId);

  return {
    orgId,
    eventId,
    credentials
  };
};

const getIds = function*() {
  const orgId = yield select(getters.orgId);
  const eventId = yield select(getters.eventId);
  const moduleId = yield select(getters.moduleId);
  const fieldId = yield select(getters.fieldId);

  return {
    eventId,
    moduleId,
    orgId,
    fieldId
  };
};

const init = function*({ payload: props }) {
  const { orgId: orgIdGet, eventId: eventIdGet } = yield call(getParams);

  try {
    const {
      module: { fields, field_groups }
    } = yield call(apiCall, {
      method: "get",
      url: `/modules/${props.moduleId}`,
      qs: {
        orgId: props.orgId || orgIdGet,
        eventId: props.eventId || eventIdGet
      }
    });

    if (!props.fieldId) {
      yield put(
        actions.setInitialData({
          moduleId: props.moduleId,
          orgId: R.propOr("", "orgId", props) || orgIdGet,
          eventId: R.propOr("", "eventId", props) || eventIdGet,
          fields,
          fieldGroups: field_groups
        })
      );
    } else {
      yield put(
        actions.setInitialEditData({
          moduleId: props.moduleId,
          orgId: R.propOr("", "orgId", props) || orgIdGet,
          eventId: R.propOr("", "eventId", props) || eventIdGet,
          fieldId: props.fieldId,
          fields,
          fieldGroups: field_groups
        })
      );
    }
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred"
        }
      ])
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const getDataForForm = function*(formIndex) {
  const form = yield select(state => getCurrentForm(state, { formIndex }));
  const showMaxLength = yield select(state =>
    getShowMaxLength(state, { formIndex })
  );
  const maxLength = yield select(state =>
    getMaxLengthFormatted(state, { formIndex })
  );

  const {
    name,
    type,
    fieldGroup,
    description,
    textSettings,
    dropdownSettings,
    dateSettings,
    dateTimeSettings,
    percentSettings,
    calculatedNumberSettings,
    currencySettings,
    lookupSettings,
    referenceSettings,
    phoneSettings,
    cateringSettings,
    fileSettings
  } = form;

  const data = {
    name,
    type,
    fieldGroup,
    settings: {
      ...R.propOr({}, type, {
        dropdown: {
          allowMultipleSelect: dropdownSettings.allowMultipleSelect,
          options: (dropdownSettings.options || []).filter(
            option => option.value
          )
        },
        date: dateSettings,
        datetime: dateTimeSettings,
        percent: percentSettings,
        "calculated-number": calculatedNumberSettings,
        currency: currencySettings,
        lookup: lookupSettings,
        reference: referenceSettings,
        phone: phoneSettings,
        text: {
          readOnly: textSettings.readOnly
        },
        catering: cateringSettings,
        file: fileSettings
      }),
      description,
      ...(showMaxLength && maxLength > 0 ? { maxLength } : {})
    }
  };

  return data;
};

const editFormField = function*({ formIndex, onSave, isInductionFormField }) {
  try {
    yield put(actions.setLoading(true));
    const data = yield call(getDataForForm, formIndex);
    const isQuestionField = data.type === "dropdown" && isInductionFormField;
    data.settings = {
      ...data.settings,
      isQuestionField
    };
    const { fieldId, moduleId, orgId, eventId } = yield call(getIds);
    const { credentials } = yield call(getParams);

    yield call(Api.updateField, {
      credentials,
      data: {
        moduleId,
        field: {
          id: fieldId,
          name: data.name,
          type: data.type,
          settings: {
            ...data.settings,
            isQuestionField
          }
        },
        options: {
          orgId,
          eventId
        }
      }
    });

    const oldFieldGroup = yield select(getters.oldFieldGroup);
    const fieldGroups = yield select(getFieldGroups, moduleId);

    if (data.fieldGroup !== R.toString(oldFieldGroup.id)) {
      const fieldGroup = fieldGroups.find(
        fg => R.toString(fg.id) === R.toString(data.fieldGroup)
      );
      const fieldGroupField = oldFieldGroup.fields.find(
        fgf => fgf.field_id === fieldId
      );

      const isDefaultGroup = id => R.toString(id) === "0";
      if (
        !isDefaultGroup(oldFieldGroup.id) &&
        !isDefaultGroup(data.fieldGroup)
      ) {
        yield call(Api.updateFieldGroup, {
          credentials,
          id: fieldGroupField.id,
          data: {
            moduleId,
            fieldId,
            groupId: data.fieldGroup,
            order: R.pathOr(0, ["fields", "length"], fieldGroup)
          }
        });
      } else if (
        isDefaultGroup(oldFieldGroup.id) &&
        !isDefaultGroup(data.fieldGroup)
      ) {
        yield call(Api.addFieldGroupField, {
          credentials,
          data: {
            moduleId,
            fieldId,
            groupId: data.fieldGroup,
            order: R.pathOr(0, ["fields", "length"], fieldGroup)
          }
        });
      } else if (
        !isDefaultGroup(oldFieldGroup.id) &&
        isDefaultGroup(data.fieldGroup)
      ) {
        // Custom group -> Standard
        yield call(Api.deleteFieldGroupField, {
          id: fieldGroupField.id,
          moduleId,
          credentials
        });
      }
    }

    yield call(onSave, data);
    yield put(hideModal());
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred"
        }
      ])
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const addFormFields = function*({ fields, fieldGroups, onSave }) {
  try {
    const { moduleId, orgId, eventId } = yield call(getIds);
    const { credentials } = yield call(getParams);

    // Add multiple fields using bulk API
    const response = yield call(Api.addBulkFields, {
      credentials,
      data: {
        moduleId,
        fields,
        options: {
          orgId,
          eventId
        }
      }
    });

    if (response && Array.isArray(response.field)) {
      // Add fields to their respective groups
      for (const field of response.field) {
        const fieldData = fields.find(f => f.name === field.name);
        if (fieldData && String(fieldData.fieldGroup) !== String(0)) {
          const fieldGroupToAddTo = fieldGroups.find(
            fg => fg.id === fieldData.fieldGroup
          );
          yield call(Api.addFieldGroupField, {
            credentials,
            data: {
              moduleId,
              fieldId: field.id,
              groupId: fieldData.fieldGroup,
              order: R.pathOr(0, ["fields", "length"], fieldGroupToAddTo)
            }
          });
        }
      }
      if (onSave) {
        yield call(onSave, response.fields);
      }
    }

    return response;
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while adding fields"
        }
      ])
    );
    throw error;
  }
};

const handleSave = function*({ payload: { onSave, isInductionFormField } }) {
  try {
    yield put(actions.setLoading(true));

    const forms = yield select(getters.forms);
    const { fieldId, moduleId, orgId, eventId } = yield call(getIds);
    const fieldGroups = yield select(getters.fieldGroups);

    // Validate forms array (may be I'll remove later if not needed)
    if (!Array.isArray(forms) || forms.length === 0) {
      yield put(
        registerError([
          {
            system: new Error("Forms data is invalid or empty"),
            user: "There are no valid forms to save. Please try again."
          }
        ])
      );
      return;
    }

    const hasError = yield select(hasFormErrors);
    if (hasError) {
      return;
    }

    //Get layout for all fields
    const { credentials } = yield call(getParams);
    const { layout } = yield call(Api.getLayout, {
      credentials,
      moduleId,
      options: { orgId, eventId }
    });

    //Collect all forms data
    const fields = [];
    for (let i = 0; i < forms.length; i++) {
      const data = yield call(getDataForForm, i);
      const isQuestionField = data.type === "dropdown" && isInductionFormField;
      data.settings = {
        ...data.settings,
        isQuestionField
      };
      fields.push({
        ...data,
        layoutId: layout.id
      });
    }

    // Save the fields based on the mode (add/edit)
    if (R.isEmpty(fieldId)) {
      // New fields mode - add multiple fields
      yield call(addFormFields, { fields, fieldGroups, onSave });
    } else {
      // Edit mode - handle single field edit
      const result = yield call(editFormField, {
        formIndex: 0,
        onSave,
        isInductionFormField
      });
      if (onSave) {
        yield call(onSave, result);
      }
    }

    // Close the modal on success
    yield put(hideModal());
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while saving fields"
        }
      ])
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchSave = function*() {
  yield takeEvery(actions.handleSave.type, handleSave);
};

const rootSaga = function*() {
  yield all([fork(watchInit), fork(watchSave)]);
};

export default rootSaga;
